import React, { useState, useRef, useEffect } from 'react';
import countryList from 'country-list';
import starIcon from '../assets/images/star.svg';
import '../assets/css/form.css';

const FormSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    website: '' // honeypot — real people never see or fill this
  });

  // 'idle' | 'submitting' | 'success' | 'error'
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef(null);
  const countries = countryList.getData();

  // Cloudflare resolves the visitor's country from their connection. Use it
  // only as an initial default so a manual selection always wins.
  useEffect(() => {
    const controller = new AbortController();

    const preselectCountry = async () => {
      try {
        const response = await fetch('/api/location', {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) return;

        const { countryCode } = await response.json();
        const countryName = countryList.getName(
          String(countryCode || '').toUpperCase()
        );
        if (!countryName) return;

        setFormData(previous => (
          previous.country
            ? previous
            : { ...previous, country: countryName }
        ));
      } catch {
        // Country detection is optional; keep the unselected fallback.
      }
    };

    preselectCountry();
    return () => controller.abort();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCountrySelect = (countryName) => {
    setFormData(prev => ({
      ...prev,
      country: countryName
    }));
    setIsSelectOpen(false);
  };

  const toggleSelect = () => {
    if (status === 'submitting') return;
    setIsSelectOpen(!isSelectOpen);
  };

  // Close select when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsSelectOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;

    // The form is noValidate so we own the messaging; that means the `required`
    // attributes don't block submission and every check has to happen here.
    const name = formData.name.trim();
    const email = formData.email.trim();

    if (!name) {
      setStatus('error');
      setErrorMessage('Please enter your name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      // Same-origin Worker endpoint, which forwards to Listmonk. The browser
      // cannot post to Listmonk directly — it does not answer CORS preflight.
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name,
          country: formData.country,
          website: formData.website
        })
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && result.ok) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage(result.error || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMessage("Couldn't reach the mailing list. Please try again.");
    }
  };

  return (
    <section id="signup" className="form-section section-breakpoint">
      <div className="container">
        <div className="star-container">
          <img src={starIcon} alt="Star" className="star-icon" />
        </div>

        <h1 className="title">
          We're starting fresh. Join our new mailing list to be the first to hear about upcoming Breakspoll
          events, merch, and voting.
        </h1>

        <div className="form-container">
          {status === 'success' ? (
            <div className="form-success" role="status">
              <p className="form-success-heading">CHECK YOUR INBOX</p>
              <p className="form-success-body">
                We've sent you a confirmation email. Click the link inside it to finish
                joining the Breakspoll mailing list.
              </p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* Honeypot: off-screen and skipped by keyboard, so only bots fill it. */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="website">Leave this field empty</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={formData.website}
                onChange={handleInputChange}
              />
            </div>

            <div className="input-wrapper">
              <input
                type="text"
                name="name"
                placeholder="YOUR NAME"
                aria-label="Your name"
                autoComplete="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={status === 'submitting'}
                required
              />
            </div>

            <div className="input-wrapper">
              <input
                type="email"
                name="email"
                placeholder="YOUR EMAIL"
                aria-label="Your email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={status === 'submitting'}
                aria-invalid={status === 'error'}
                aria-describedby={status === 'error' ? 'form-error' : undefined}
                required
              />
            </div>

            <div className="input-wrapper">
              <div className="custom-select" ref={selectRef}>
                <div
                  className="select-trigger"
                  onClick={toggleSelect}
                  style={{
                    display: formData.country ? 'block' : 'none'
                  }}
                >
                  {formData.country || 'YOUR COUNTRY'}
                </div>
                <div
                  className="select-placeholder"
                  onClick={toggleSelect}
                  style={{
                    display: formData.country ? 'none' : 'block'
                  }}
                >
                YOUR COUNTRY
                </div>
                {isSelectOpen && (
                  <div className="select-dropdown">
                    {countries.map(({ name, code }) => (
                      <div
                        key={code}
                        className="select-option"
                        onClick={() => handleCountrySelect(name)}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {status === 'error' && (
              <p className="form-error" id="form-error" role="alert">
                {errorMessage}
              </p>
            )}

            <button type="submit" disabled={status === 'submitting'}>
              {status === 'submitting' ? 'SUBMITTING…' : 'SUBMIT'}
            </button>
          </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default FormSection;
